import { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { getRequest } from '../Fetch/request';
import { refreshAuthToken, logout as logoutUtil, getAuthToken, isUserAuthenticated } from '../Utils/authUtils';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);



  // Function to check authentication state
  const checkAuthState = async (firebaseUser) => {
    setIsLoading(true);
    
    if (firebaseUser) {
      // User is signed in with Firebase
      setUser(firebaseUser);
      
      // Check if we have backend profile data and valid JWT
      const profileObject = localStorage.getItem('profileObject');
      const userId = localStorage.getItem('userId');
      const authToken = getAuthToken();
      
      if (profileObject && userId && authToken) {
        // Token validation removed - assume token is valid if it exists
        // Cache successful validation
        localStorage.setItem('lastTokenValidation', Date.now().toString());
        setIsAuthenticated(true);
      } else {
        // Firebase user exists but no backend data - need to re-authenticate
        setUser(null);
        setIsAuthenticated(false);
        // Clear any partial data
        localStorage.removeItem('email');
        localStorage.removeItem('userId');
        localStorage.removeItem('profileObject');
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
      }
    } else {
      // User is signed out
      setUser(null);
      setIsAuthenticated(false);
      // Clear all auth data
      localStorage.removeItem('email');
      localStorage.removeItem('userId');
      localStorage.removeItem('profileObject');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    }
    
    setIsLoading(false);
  };

  useEffect(() => {
    // Initial authentication check on mount
    const authToken = getAuthToken();
    const userId = localStorage.getItem('userId');
    const profileObject = localStorage.getItem('profileObject');
    
    if (authToken && userId && profileObject) {
      setIsAuthenticated(true);
      setIsLoading(false);
    }
    
    const unsubscribe = onAuthStateChanged(auth, checkAuthState);

    // Listen for storage changes (when login happens in another tab or after page reload)
    const handleStorageChange = () => {
      const newAuthToken = getAuthToken();
      const newUserId = localStorage.getItem('userId');
      const newProfileObject = localStorage.getItem('profileObject');
      
      if (newAuthToken && newUserId && newProfileObject) {
        setIsAuthenticated(true);
        setIsLoading(false);
      } else {
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup subscription
    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Add empty dependency array, []);



  const logout = useCallback(async () => {
    try {
      // Use centralized logout utility
      logoutUtil();
      
      // Sign out from Firebase
      await signOut(auth);
      // Firebase onAuthStateChanged will handle the rest
    } catch (error) {
    }
  }, []);

  const getUserId = () => {
    return localStorage.getItem('userId');
  };

  const getEmail = () => {
    return localStorage.getItem('email');
  };

  const getProfileObject = () => {
    const profile = localStorage.getItem('profileObject');
    return profile ? JSON.parse(profile) : null;
  };

  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  return {
    user,
    isAuthenticated, // Use state value instead of function call
    isLoading,
    logout,
    getUserId,
    getEmail,
    getProfileObject,
    getAuthToken,
  };
}; 